// Web Push on this device: permission → service worker → PushManager subscription → saved on the server.
import apiClient from "@/lib/api";

const b64ToBytes = (v) => {
  const pad = "=".repeat((4 - (v.length % 4)) % 4);
  const raw = atob((v + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const pushSupported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
export const permissionState = () => (typeof Notification === "undefined" ? "unsupported" : Notification.permission);

async function registration() {
  const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  await navigator.serviceWorker.ready;
  return reg;
}

export async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
}

// Must be called from a user gesture (iOS). Returns the server-side subscription record.
export async function enablePush({ region = "UK", prefs } = {}) {
  if (!pushSupported()) throw new Error("This browser can't receive push notifications");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error(permission === "denied" ? "Notifications are blocked in your browser settings" : "Permission not granted");
  const reg = await registration();
  const { data } = await apiClient.get("/push/public-key");
  if (!data?.publicKey) throw new Error("Push isn't configured on the server");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(data.publicKey) });
  const r = await apiClient.post("/push/subscribe", { ...sub.toJSON(), region, ua: navigator.userAgent.slice(0, 200), prefs });
  return r.data.subscription;
}

export async function disablePush() {
  const sub = await currentSubscription();
  if (!sub) return;
  try { await apiClient.delete("/push/subscribe", { params: { endpoint: sub.endpoint } }); } catch { /* server record may already be gone */ }
  await sub.unsubscribe();
}

export async function pushStatus() {
  const sub = await currentSubscription();
  if (!sub) return { subscribed: false, subscription: null };
  const { data } = await apiClient.get("/push/status", { params: { endpoint: sub.endpoint } });
  return data;
}

export async function setPushPrefs(prefs, region) {
  const sub = await currentSubscription();
  if (!sub) throw new Error("Notifications are off on this device");
  const { data } = await apiClient.put("/push/prefs", { endpoint: sub.endpoint, prefs, region });
  return data.subscription;
}

export async function sendTestPush() {
  const sub = await currentSubscription();
  if (!sub) throw new Error("Notifications are off on this device");
  const { data } = await apiClient.post("/push/test", null, { params: { endpoint: sub.endpoint } });
  return data;
}

// Sign-out on a device switches its notifications off (server record removed; browser subscription dropped).
export async function forgetPushOnLogout() {
  try { await disablePush(); } catch { /* best effort */ }
}
